import { User } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Paginator from "../../components/admin/shared/Paginator";
import SectionCard from "../../components/admin/shared/SectionCard";
import AsyncState from "../../components/admin/shared/AsyncState";
import AdminTable, { AdminTableColumn } from "../../components/admin/shared/AdminTable";
import { fetchAdminLeagues, AdminLeague } from "../../api/admin";
import { useAdminSort } from "../../hooks/useAdminSort";
import { inputStyle, smallBtn } from "../../styles";

interface Props {
  user: User;
}

const PAGE_SIZE = 25;

export default function LeaguesPage({ user }: Props) {
  const { t } = useTranslation();
  const [leagues, setLeagues] = useState<AdminLeague[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { sort, toggleSort } = useAdminSort();

  const load = async (s: string, p: number) => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminLeagues(user, { search: s || undefined, page: p, sortBy: sort.sortBy ?? undefined, sortDir: sort.sortDir });
      setLeagues(data.leagues);
      setTotal(data.total);
    } catch {
      setError(t("admin.leagues.errors.failedToLoad"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load("", 1); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); load(search, 1); }, 300);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(search, page); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(1); load(search, 1); }, [sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const columns: AdminTableColumn<AdminLeague>[] = [
    { key: "name", label: t("admin.leagues.columns.name"), sortKey: "name", render: (l) => l.name },
    {
      key: "competition",
      label: t("admin.leagues.columns.competition"),
      sortKey: "competition",
      render: (l) => (
        <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
          {l.competition.name} · {l.competition.season}
        </span>
      ),
    },
    { key: "admin", label: t("admin.leagues.columns.admin"), sortKey: "admin", render: (l) => <span style={{ fontSize: "0.85rem" }}>{l.admin.displayName}</span> },
    { key: "members", label: t("admin.leagues.columns.memberCount"), align: "center", sortKey: "members", render: (l) => l._count.members },
    {
      key: "actions",
      label: "",
      render: (l) => (
        <Link to={`/admin/leagues/${l.id}`} style={{ ...smallBtn, textDecoration: "none", display: "inline-block" }}>
          {t("admin.leagues.manage")}
        </Link>
      ),
    },
  ];

  return (
    <div>
      <SectionCard
        title={t("admin.tabs.leagues")}
        actions={
          <input
            style={{ ...inputStyle, width: "220px" }}
            placeholder={t("admin.leagues.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        }
      >
        <AsyncState loading={loading} error={error}>
          <AdminTable columns={columns} rows={leagues} rowKey={(l) => l.id} sort={sort} onSort={toggleSort} />
        </AsyncState>

        <Paginator page={page} totalPages={totalPages} onPageChange={setPage} />
      </SectionCard>
    </div>
  );
}
